import { Component, ChangeDetectionStrategy, signal, HostListener, inject, OnInit } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { MatSidenavModule } from '@angular/material/sidenav';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbService } from '../../../../core/services/breadcrumb.service';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent,
    RouterOutlet,
    MatSidenavModule,
    BreadcrumbComponent],
  templateUrl: './admin-layout.component.html',
  styleUrl: './admin-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminLayoutComponent implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  protected breadcrumbService = inject(BreadcrumbService);

  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  menuConfig: SidebarItem[] = [
    { id: 'admin_dashboard', label: 'Dashboard', icon: 'dashboard', route: '/admin', exact: true },
    {
      id: 'resources_management',
      label: 'Resources Management',
      icon: 'inventory_2',
      children: [
        { id: 'admin_resources', label: 'Resource Registry', icon: 'inventory_2', route: '/admin/resources' },
        { id: 'admin_actions', label: 'Action Registry', icon: 'gavel', route: '/admin/actions' },

      ]
    },
    {
      id: 'user_management',
      label: 'User Management',
      icon: 'people',
      children: [
        { id: 'admin_roles', label: 'Role Management', icon: 'security', route: '/admin/roles' },
        { id: 'admin_users', label: 'User Mapping', icon: 'manage_accounts', route: '/admin/users' },
        { id: 'admin_dsc_registration', label: 'DSC Registration', icon: 'badge', route: '/admin/dsc-registration' },
        { id: 'admin_workflow', label: 'Workflow Master', icon: 'alt_route', route: '/admin/workflow-master' },
        { id: 'admin_post_assignment', label: 'Post Assignment', icon: 'assignment_ind', route: '/admin/post-assignment' },


      ]
    },

    {
      id: 'master_setup',
      label: 'Master Setup',
      icon: 'show_chart',
      children: [
        { id: 'admin_fields', label: 'Field Info', icon: 'text_fields', route: '/admin/fields' },
        { id: 'admin_codevalues', label: 'Code Values', icon: 'data_object', route: '/admin/code-values' },
        { id: 'admin_hierarchy', label: 'Organization Hierarchy', icon: 'account_tree', route: '/admin/hierarchy' },
        { id: 'admin_prod_hier', label: 'Product Hierarchy', icon: 'category', route: '/admin/product-hierarchy' },
        { id: 'admin_act_hier', label: 'Activity Hierarchy', icon: 'account_tree', route: '/admin/activity-hierarchy' },
        { id: 'admin_screen_rule_mapping', label: 'Hierarchy Mapping', icon: 'settings_suggest', route: '/admin/screen-rule-mapping' },
        { id: 'admin_salary_coa_mapping', label: 'COA Mapping', icon: 'account_balance', route: '/admin/salary-coa-mapping' },

      ]
    },

    //{ id: 'admin_security', label: 'Security Settings', icon: 'lock', route: '/admin/security' },
    //{ id: 'admin_health', label: 'System Health', icon: 'health_and_safety', route: '/admin/health' }
  ];

  ngOnInit() {
    this.resolvePermissionsFromRouter();
  }

  private resolvePermissionsFromRouter(): void {
    const adminRoute = this.router.config.find(r => r.path === 'admin');
    this.menuConfig = resolveMenuPermissionsFromRoute(this.menuConfig, adminRoute, 'admin');
  }

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }
}
